import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { addSnmpHost, snmpHostDetailsQuery, snmpHostFormQuery, snmpHostsTableQuery } from '../api/proactivitee'
import { stamp } from '../inventoree/format'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { StatusPane } from '../devices/DeviceExternalMonitoring'
import { hostsColumns } from './snmpColumns'

/**
 * SNMP Hosts - `#snmpHosts` plus Add host. Titlul ferestrei
 * "Watch a device from outside" e formularul de add-host, nu un ecran Watch.
 */

export function SnmpHostsPage() {
  const [state, setState] = useTableState('snmpHosts', { start: 0, length: 25, order: { column: 1, dir: 'asc' }, search: '' })
  const [addOpen, setAddOpen] = useState(false)
  const columns = useMemo(() => hostsColumns(), [])
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[1]
  const query = useQuery({
    ...snmpHostsTableQuery({
      draw: 1, start: state.start, length: state.length,
      orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const canAdd = Boolean(query.data?.canAdd)

  return (
    <Content>
      <div id="content">
        <DataTable
          id="snmpHosts"
          columnTools
          columns={columns}
          rows={query.data?.data ?? []}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          details={(row) => <HostTabs deviceId={Number(row.deviceId)} />}
          /*
           * Add host in BARA DE SUS, nu intr-o banda proprie deasupra tabelului.
           *
           * In portal butonul statea in `.em-add-bar`, o bucata de macheta
           * adaugata pe 2 septembrie 2026 pentru urmarirea din exterior si
           * folosita numai de ea. A ramas deasupra tabelului fiindca sta in
           * AFARA invelisului DataTables, iar `sweepStrip` din list_chrome.js -
           * cel care goleste banda de deasupra listei si urca butoanele
           * modulului in `li.tk-header-tools` - se uita numai la copiii
           * invelisului. Regula scrisa acolo e ca in banda aia nu ramane nimic;
           * butonul asta n-a scapat de ea pentru ca era o exceptie, ci pentru
           * ca maturatorul nu ajungea la el.
           *
           * Aici nu mai e nimic de maturat: butonul se deseneaza direct unde il
           * pun celelalte douazeci si patru de liste din v4.
           */
          headerButtons={
            canAdd ? (
              <button
                type="button"
                className="btn btn-primary em-add-open tk-header-extra"
                onClick={() => setAddOpen(true)}
              >
                <i className="fa fa-plus" /> Add host
              </button>
            ) : undefined
          }
          /* `buttons: DataTablesProperties.buttons` in SnmpHostsDatatable:
             Copy, Columns si Refresh, pe care list_chrome le coboara in subsol. */
          footerButtons={{ copy: true, colvis: true, refresh: () => void query.refetch() }}
        >
          {canAdd && addOpen ? (
            <AddHostModal onClose={() => setAddOpen(false)} onAdded={() => void query.refetch()} />
          ) : null}
        </DataTable>
      </div>
    </Content>
  )
}

interface HostTab {
  slug: string
  label: string
  allowed: boolean
}

/** 0 = UP, orice altceva = DOWN; lipsa randului = masina nu e urmarita deloc. */
function StatusCell({ row }: { row: Record<string, unknown> | null }) {
  if (row === null || row.status == null) {
    return (
      <td>
        <center data-toggle="tooltip" data-placement="top" title="An error occured! This host is not monitored!">
          <i className="fa fa-question-circle font-20 color-red"></i>
        </center>
      </td>
    )
  }
  const up = Number(row.status) === 0
  return (
    <td>
      <center>
        <i
          className={`fas ${up ? 'fa-check-circle font-20 color-green' : 'fa-minus-circle font-20 color-red'}`}
          data-toggle="tooltip"
          data-placement="top"
          title={up ? 'UP' : 'DOWN'}
        ></i>
      </center>
    </td>
  )
}

/** date('Y-m-d') / date('H:i') pe outage_date, sau "n/a" cand n-a cazut niciodata. */
function outageParts(raw: unknown): [string, string] {
  const when = stamp(raw)
  if (!when) return ['n/a', 'n/a']
  const two = (n: number) => String(n).padStart(2, '0')
  return [
    `${when.getFullYear()}-${two(when.getMonth() + 1)}-${two(when.getDate())}`,
    `${two(when.getHours())}:${two(when.getMinutes())}`,
  ]
}

function HostRow({ name, row, red }: { name: string; row: Record<string, unknown> | null; red: boolean }) {
  const known = row !== null && row.status != null
  const [date, time] = outageParts(row?.outage_date)
  return (
    <tr>
      <td>{red ? <span className="color-red">{name}</span> : <span>{name}</span>}</td>
      <StatusCell row={row} />
      <td><center>{known ? (Number(row!.status) === 0 ? 'UP' : 'DOWN') : <em>n/a</em>}</center></td>
      <td><center>{known ? date : <em>n/a</em>}</center></td>
      <td><center>{known ? time : <em>n/a</em>}</center></td>
    </tr>
  )
}

/**
 * Randul desfacut al unei gazde - hosts/tabList.php. Doua file, fiecare cu
 * dreptul ei, si un tabel adevarat: gazda plus masinile virtuale de sub ea.
 * Se desenau doar numele si un "Status: 1", iar masinile virtuale - pe care
 * serverul le trimitea deja - nu se vedeau deloc.
 */
function HostTabs({ deviceId }: { deviceId: number }) {
  const query = useQuery(snmpHostDetailsQuery(deviceId))
  const [picked, setPicked] = useState<string | null>(null)
  const device = (query.data?.device ?? null) as Record<string, unknown> | null
  const rawHost = (query.data?.host ?? null) as Record<string, unknown> | null
  const host = rawHost && Object.keys(rawHost).length > 0 ? rawHost : null
  const vms = (query.data?.vms ?? []) as Record<string, unknown>[]
  const tabs = ((query.data?.tabs ?? []) as HostTab[]).filter((t) => t.allowed)

  if (query.isPending) {
    return <div className="details_content"><i className="fas fa-circle-notch fa-spin"></i> Loading...</div>
  }
  if (tabs.length === 0) {
    return <div className="alert alert-danger" role="alert">You do not have access to this action.</div>
  }
  const active = picked && tabs.some((t) => t.slug === picked) ? picked : tabs[0].slug

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.map((t) => (
            <li key={t.slug}>
              <a
                href={`#${t.slug}`}
                className={`action_button${t.slug === active ? ' color-red' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  setPicked(t.slug)
                }}
              >
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {active === 'services' ? (
            <StatusPane id={deviceId} />
          ) : (
            <div className="row">
              <div className="col-md-12">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Device Name</th>
                      <th>Status</th>
                      <th>Last Change</th>
                      <th>Last Outage Date</th>
                      <th>Last Outage Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <HostRow name={String(device?.Denumire ?? '')} row={host} red />
                    {vms.map((vm) => (
                      <HostRow key={String(vm.device_id)} name={String(vm.device_name ?? '')} row={vm.status == null ? null : vm} red={false} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Fereastra de adaugare. Se monteaza doar cat sta deschisa, deci nu-si mai tine
 * singura steagul `open`: butonul care o deschide a urcat in bara de sus si nu
 * mai e fratele ei.
 */
function AddHostModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const form = useQuery(snmpHostFormQuery())
  const [error, setError] = useState('')
  const [ip, setIp] = useState('')
  const candidates = form.data?.candidates ?? []
  const probes = form.data?.probes ?? []
  const communities = form.data?.communities ?? []
  /* Tipurile fara bifa "Remote Monitoring" (Device types) nu se pot pune sub
     urmarire: raman in lista, dar neselectabile, cu motivul scris. */
  const blocked = candidates.filter((d) => d.type_remote_monitoring === false)

  async function save() {
    const device = (document.getElementById('ah-device') as HTMLSelectElement | null)?.value ?? ''
    const server = (document.getElementById('ah-server') as HTMLSelectElement | null)?.value ?? ''
    const community = (document.getElementById('ah-community') as HTMLSelectElement | null)?.value ?? '0'
    const port = (document.getElementById('ah-port') as HTMLInputElement | null)?.value ?? '161'
    const result = await addSnmpHost({ device, ip, server, community, udp_port: port })
    if (result.status === 'OK') {
      onClose()
      onAdded()
      return
    }
    setError(result.message)
  }

  return (
    <div className="modal fade in" id="emAdd" tabIndex={-1} role="dialog" style={{ display: 'block' }}>
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="close" aria-label="Close" onClick={onClose}><span aria-hidden="true">&times;</span></button>
            <h4 className="modal-title" id="emAddTitle">Watch a device from outside</h4>
          </div>
          <div className="modal-body">
            {error ? <div className="alert alert-danger" id="emAddError">{error}</div> : <div className="alert alert-danger" id="emAddError" style={{ display: 'none' }} />}
            <div id="emAddBody">
              <form id="add-host-form">
                {candidates.length === 0 ? (
                  <div className="alert alert-info" role="alert">
                    <p style={{ marginBottom: 0 }}>Every device in this company is already watched from outside.</p>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label htmlFor="ah-device">Device</label>
                      <select name="device" id="ah-device" className="form-control" required
                        onChange={(e) => {
                          const opt = e.target.selectedOptions[0]
                          if (opt && !ip) setIp(opt.getAttribute('data-ip') ?? '')
                        }}
                      >
                        <option value="">Pick a device...</option>
                        {candidates.map((d) => (
                          <option key={String(d.ID)} value={String(d.ID)} data-ip={String(d.ip ?? '')} disabled={d.type_remote_monitoring === false}>
                            {String(d.Denumire)}{d.type_name ? ` · ${String(d.type_name)}` : ''}
                            {d.type_remote_monitoring === false ? ' (type not enabled for Remote Monitoring)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {blocked.length > 0 && (
                      <div className="alert alert-warning" id="emAddTypeOff" role="note">
                        <i className="fas fa-info-circle" aria-hidden="true"></i> {blocked.length === 1 ? '1 device cannot' : `${blocked.length} devices cannot`} be
                        picked because their type is not enabled for Remote Monitoring. Turn it on for the type in{' '}
                        <a href="/app/settings/inventoree/device_types">Settings &gt; Inventoree &gt; Device types</a>.
                      </div>
                    )}
                    <div className="row">
                      <div className="col-md-8">
                        <div className="form-group">
                          <label htmlFor="ah-ip">Address to watch</label>
                          <input type="text" name="ip" id="ah-ip" className="form-control" placeholder="10.0.0.1 or host.name" required value={ip} onChange={(e) => setIp(e.target.value)} />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="ah-port">UDP port</label>
                          <input type="number" name="udp_port" id="ah-port" className="form-control" defaultValue={161} min={1} max={65535} />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="ah-server">Watched from</label>
                          <select name="server" id="ah-server" className="form-control" required>
                            <option value="">Pick a probe...</option>
                            {probes.map((p) => (
                              <option key={String(p.ID)} value={String(p.ID)}>
                                {String(p.name)}{p.ip ? ` (${String(p.ip)})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="ah-community">SNMP community <small className="text-muted">(optional)</small></label>
                          <select name="community" id="ah-community" className="form-control">
                            <option value="0">None</option>
                            {communities.map((c) => (
                              <option key={String(c.ID)} value={String(c.ID)}>{String(c.name)}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-default" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" id="emAddSave" onClick={() => void save()}>Add</button>
          </div>
        </div>
      </div>
    </div>
  )
}
